// Enhanced help component with full management interface

import React, { useState } from 'react';
import { 
  Button, Space, Input, Spin, Alert, Card, Typography, List, Badge, Breadcrumb,
  Tabs, Table, Modal, Form, Select, InputNumber, Switch, Tag, Popconfirm, message
} from 'antd';
import { 
  QuestionCircleOutlined, SettingOutlined, SearchOutlined, MenuFoldOutlined, MenuUnfoldOutlined,
  HomeOutlined, BookOutlined, FileTextOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  SaveOutlined, CloseOutlined
} from '@ant-design/icons';
import { useAuth } from '~/store';
import { Role } from '~/utils/constants';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { baseService } from "~/services/base";

const { Search } = Input;
const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

// Simple markdown-to-HTML converter
const renderMarkdown = (content: string) => {
  let html = content
    .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-6 mb-3 text-gray-800">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mt-8 mb-4 text-gray-900">$1</h2>')
    .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-8 mb-6 text-gray-900">$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
    .replace(/^- (.*$)/gim, '<li class="ml-4 mb-1">• $1</li>')
    .replace(/^(\d+)\. (.*$)/gim, '<li class="ml-4 mb-1">$1. $2</li>')
    .replace(/\n\n/g, '</p><p class="mb-4">')
    .replace(/\n/g, '<br/>');

  if (!html.startsWith('<h1>') && !html.startsWith('<h2>')) {
    html = '<p class="mb-4">' + html + '</p>';
  }
  return html;
};

export const HelpResources: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isManagementMode, setIsManagementMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  
  // Management state
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [articleModalVisible, setArticleModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [editingArticle, setEditingArticle] = useState<any>(null);
  const [managementTab, setManagementTab] = useState('categories');
  
  const [categoryForm] = Form.useForm();
  const [articleForm] = Form.useForm();

  const isSuperAdmin = user?.role === Role.SUPER_ADMIN || user?.role === Role.SYSTEM_ADMIN;
  const isPlatformOwner = !!user?.organization?.isPlatformOwner;

  // Queries
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['help-categories'],
    queryFn: async () => {
      const response = await baseService.get('/help-categories');
      return response.data;
    },
  });

  const { data: articles, isLoading: articlesLoading } = useQuery({
    queryKey: ['help-articles', selectedCategoryId],
    queryFn: async () => {
      const params = selectedCategoryId ? { categoryId: selectedCategoryId } : {};
      const response = await baseService.get('/help-articles', { params });
      return response.data;
    },
  });

  const { data: allArticles } = useQuery({
    queryKey: ['all-help-articles'],
    queryFn: async () => {
      const response = await baseService.get('/help-articles');
      return response.data;
    },
    enabled: isManagementMode,
  });

  const { data: selectedArticle } = useQuery({
    queryKey: ['help-article', selectedArticleId],
    queryFn: async () => {
      if (!selectedArticleId) return null;
      const response = await baseService.get(`/help-articles/${selectedArticleId}`);
      return response.data;
    },
    enabled: !!selectedArticleId,
  });

  // Mutations
  const createCategoryMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await baseService.post('/help-categories', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['help-categories'] });
      message.success('Category created successfully');
      setCategoryModalVisible(false);
      categoryForm.resetFields();
    },
    onError: (error) => {
      message.error('Failed to create category');
      console.error(error);
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await baseService.put(`/help-categories/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['help-categories'] });
      message.success('Category updated successfully');
      setCategoryModalVisible(false);
      setEditingCategory(null);
      categoryForm.resetFields();
    },
    onError: (error) => {
      message.error('Failed to update category');
      console.error(error);
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: string) => {
      await baseService.delete(`/help-categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['help-categories'] });
      message.success('Category deleted successfully');
    },
    onError: (error) => {
      message.error('Failed to delete category');
      console.error(error);
    },
  });

  const createArticleMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await baseService.post('/help-articles', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
      queryClient.invalidateQueries({ queryKey: ['all-help-articles'] });
      message.success('Article created successfully');
      setArticleModalVisible(false);
      articleForm.resetFields();
    },
    onError: (error) => {
      message.error('Failed to create article');
      console.error(error);
    },
  });

  const updateArticleMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await baseService.put(`/help-articles/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
      queryClient.invalidateQueries({ queryKey: ['all-help-articles'] });
      message.success('Article updated successfully');
      setArticleModalVisible(false);
      setEditingArticle(null);
      articleForm.resetFields();
    },
    onError: (error) => {
      message.error('Failed to update article');
      console.error(error);
    },
  });

  const deleteArticleMutation = useMutation({
    mutationFn: async (id: string) => {
      await baseService.delete(`/help-articles/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
      queryClient.invalidateQueries({ queryKey: ['all-help-articles'] });
      message.success('Article deleted successfully');
    },
    onError: (error) => {
      message.error('Failed to delete article');
      console.error(error);
    },
  });

  // Handlers
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setSelectedCategoryId(null);
    setSelectedArticleId(null);
  };

  const handleCreateCategory = () => {
    setEditingCategory(null);
    categoryForm.resetFields();
    categoryForm.setFieldsValue({
      sortOrder: (categories?.length || 0) + 1,
      isActive: true,
    });
    setCategoryModalVisible(true);
  };

  const handleEditCategory = (category: any) => {
    setEditingCategory(category);
    categoryForm.setFieldsValue(category);
    setCategoryModalVisible(true);
  };

  const handleCreateArticle = () => {
    setEditingArticle(null);
    articleForm.resetFields();
    articleForm.setFieldsValue({
      sortOrder: 1,
      isActive: true,
      categoryId: selectedCategoryId || undefined,
    });
    setArticleModalVisible(true);
  };

  const handleEditArticle = (article: any) => {
    setEditingArticle(article);
    articleForm.setFieldsValue({
      ...article,
      tags: article.tags || [],
    });
    setArticleModalVisible(true);
  };

  const handleCategorySubmit = async (values: any) => {
    if (editingCategory) {
      updateCategoryMutation.mutate({ id: editingCategory.id, data: values });
    } else {
      createCategoryMutation.mutate(values);
    }
  };

  const handleArticleSubmit = async (values: any) => {
    if (editingArticle) {
      updateArticleMutation.mutate({ id: editingArticle.id, data: values });
    } else {
      createArticleMutation.mutate(values);
    }
  };

  const selectedCategory = categories?.find(cat => cat.id === selectedCategoryId);
  const isLoading = categoriesLoading || articlesLoading;

  if (isLoading && !categories) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <Spin size="large" tip="Loading help resources..." />
      </div>
    );
  }

  // Management Mode UI
  const renderManagementMode = () => (
    <div className="p-6">
      <div className="mb-6">
        <Title level={2}>Help Content Management</Title>
        <Paragraph>Create, edit, and manage help categories and articles.</Paragraph>
      </div>

      <Tabs activeKey={managementTab} onChange={setManagementTab}>
        <TabPane tab="Categories" key="categories">
          <div className="mb-4">
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleCreateCategory}
            >
              Create Category
            </Button>
          </div>

          <Table
            dataSource={categories}
            rowKey="id"
            pagination={false}
            columns={[
              {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
              },
              {
                title: 'Description',
                dataIndex: 'description',
                key: 'description',
                ellipsis: true,
              },
              {
                title: 'Sort Order',
                dataIndex: 'sortOrder',
                key: 'sortOrder',
                width: 100,
              },
              {
                title: 'Active',
                dataIndex: 'isActive',
                key: 'isActive',
                width: 80,
                render: (isActive: boolean) => (
                  <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'Active' : 'Inactive'}
                  </Tag>
                ),
              },
              {
                title: 'Articles',
                key: 'articles',
                width: 80,
                render: (_, record) => (
                  <Badge count={record.articles?.length || 0} />
                ),
              },
              ...(isPlatformOwner ? [{
                title: 'Publish to Tenants',
                key: 'publish',
                width: 140,
                render: (_: any, record: any) => (
                  <Switch
                    checked={record.isPublishedToTenants}
                    onChange={(checked) => updateCategoryMutation.mutate({ id: record.id, data: { isPublishedToTenants: checked } })}
                    checkedChildren="Published"
                    unCheckedChildren="Internal"
                    size="small"
                  />
                ),
              }] : []),
              {
                title: 'Actions',
                key: 'actions',
                width: 120,
                render: (_, record) => (
                  <Space>
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => handleEditCategory(record)}
                    />
                    <Popconfirm
                      title="Delete category?"
                      description="This will also delete all articles in this category."
                      onConfirm={() => deleteCategoryMutation.mutate(record.id)}
                      okText="Delete"
                      cancelText="Cancel"
                    >
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        </TabPane>

        <TabPane tab="Articles" key="articles">
          <div className="mb-4">
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleCreateArticle}
            >
              Create Article
            </Button>
          </div>

          <Table
            dataSource={allArticles}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            columns={[
              {
                title: 'Title',
                dataIndex: 'title',
                key: 'title',
              },
              {
                title: 'Category',
                key: 'category',
                render: (_, record) => record.category?.name || 'Unknown',
              },
              {
                title: 'Sort Order',
                dataIndex: 'sortOrder',
                key: 'sortOrder',
                width: 100,
              },
              {
                title: 'Active',
                dataIndex: 'isActive',
                key: 'isActive',
                width: 80,
                render: (isActive: boolean) => (
                  <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'Active' : 'Inactive'}
                  </Tag>
                ),
              },
              {
                title: 'Tags',
                dataIndex: 'tags',
                key: 'tags',
                render: (tags: string[]) => (
                  <div>
                    {tags?.slice(0, 2).map(tag => (
                      <Tag key={tag} size="small">{tag}</Tag>
                    ))}
                    {tags?.length > 2 && <span>+{tags.length - 2} more</span>}
                  </div>
                ),
              },
              ...(isPlatformOwner ? [{
                title: 'Publish to Tenants',
                key: 'publish',
                width: 140,
                render: (_: any, record: any) => (
                  <Switch
                    checked={record.isPublishedToTenants}
                    onChange={(checked) => updateArticleMutation.mutate({ id: record.id, data: { isPublishedToTenants: checked } })}
                    checkedChildren="Published"
                    unCheckedChildren="Internal"
                    size="small"
                  />
                ),
              }] : []),
              {
                title: 'Actions',
                key: 'actions',
                width: 120,
                render: (_, record) => (
                  <Space>
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => handleEditArticle(record)}
                    />
                    <Popconfirm
                      title="Delete article?"
                      description="This action cannot be undone."
                      onConfirm={() => deleteArticleMutation.mutate(record.id)}
                      okText="Delete"
                      cancelText="Cancel"
                    >
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        </TabPane>
      </Tabs>

      {/* Category Modal */}
      <Modal
        title={editingCategory ? 'Edit Category' : 'Create Category'}
        open={categoryModalVisible}
        onCancel={() => {
          setCategoryModalVisible(false);
          setEditingCategory(null);
          categoryForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={categoryForm}
          layout="vertical"
          onFinish={handleCategorySubmit}
        >
          <Form.Item
            name="name"
            label="Category Name"
            rules={[{ required: true, message: 'Please enter category name' }]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea rows={3} placeholder="Enter category description" />
          </Form.Item>

          <Form.Item
            name="sortOrder"
            label="Sort Order"
            rules={[{ required: true, message: 'Please enter sort order' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item className="mb-0">
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createCategoryMutation.isPending || updateCategoryMutation.isPending}
                icon={<SaveOutlined />}
              >
                {editingCategory ? 'Update' : 'Create'}
              </Button>
              <Button
                onClick={() => {
                  setCategoryModalVisible(false);
                  setEditingCategory(null);
                  categoryForm.resetFields();
                }}
                icon={<CloseOutlined />}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Article Modal */}
      <Modal
        title={editingArticle ? 'Edit Article' : 'Create Article'}
        open={articleModalVisible}
        onCancel={() => {
          setArticleModalVisible(false);
          setEditingArticle(null);
          articleForm.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={articleForm}
          layout="vertical"
          onFinish={handleArticleSubmit}
        >
          <Form.Item
            name="title"
            label="Article Title"
            rules={[{ required: true, message: 'Please enter article title' }]}
          >
            <Input placeholder="Enter article title" />
          </Form.Item>

          <Form.Item
            name="categoryId"
            label="Category"
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <Select placeholder="Select category">
              {categories?.map(category => (
                <Select.Option key={category.id} value={category.id}>
                  {category.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="content"
            label="Content (Markdown supported)"
            rules={[{ required: true, message: 'Please enter article content' }]}
          >
            <TextArea 
              rows={12} 
              placeholder="Enter article content. You can use markdown formatting:&#10;# Header 1&#10;## Header 2&#10;**Bold text**&#10;*Italic text*&#10;- Bullet point"
            />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="sortOrder"
              label="Sort Order"
              rules={[{ required: true, message: 'Please enter sort order' }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="isActive"
              label="Active"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </div>

          <Form.Item
            name="tags"
            label="Tags (optional)"
          >
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Add tags (press Enter to add)"
              tokenSeparators={[',']}
            />
          </Form.Item>

          <Form.Item className="mb-0">
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createArticleMutation.isPending || updateArticleMutation.isPending}
                icon={<SaveOutlined />}
              >
                {editingArticle ? 'Update' : 'Create'}
              </Button>
              <Button
                onClick={() => {
                  setArticleModalVisible(false);
                  setEditingArticle(null);
                  articleForm.resetFields();
                }}
                icon={<CloseOutlined />}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );

  return (
    <div className="h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Button
              type="text"
              icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="mr-4"
            />
            <QuestionCircleOutlined className="text-xl mr-2 text-blue-500" />
            <h1 className="text-xl font-semibold">Help & Resources</h1>
          </div>

          <Space>
            <Search
              placeholder="Search help articles..."
              allowClear
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              onSearch={handleSearch}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
            />

            {isSuperAdmin && (
              <Button
                type={isManagementMode ? 'primary' : 'default'}
                icon={<SettingOutlined />}
                onClick={() => setIsManagementMode(!isManagementMode)}
              >
                {isManagementMode ? 'Exit Management' : 'Manage Content'}
              </Button>
            )}
          </Space>
        </div>
      </div>

      {/* Main Content Area */}
      {isManagementMode ? (
        renderManagementMode()
      ) : (
        <div className="flex h-[calc(100vh-140px)]">
          {/* Sidebar - Categories */}
          {!sidebarCollapsed && (
            <div className="w-80 bg-white border-r border-gray-200 p-4 overflow-auto">
              <div className="mb-4">
                <Title level={4} className="flex items-center gap-2">
                  <BookOutlined className="text-blue-500" />
                  Categories
                </Title>
              </div>
              
              {categories && categories.length > 0 ? (
                <List
                  dataSource={categories}
                  renderItem={(category) => (
                    <List.Item
                      className={`cursor-pointer p-3 rounded-lg border transition-all hover:shadow-sm ${
                        selectedCategoryId === category.id
                          ? 'bg-blue-50 border-blue-200'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        setSelectedCategoryId(category.id);
                        setSelectedArticleId(null);
                      }}
                    >
                      <div className="w-full">
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-gray-900">{category.name}</div>
                          <Badge
                            count={category.articles?.length || 0}
                            style={{ backgroundColor: selectedCategoryId === category.id ? '#1890ff' : '#f5f5f5', color: selectedCategoryId === category.id ? 'white' : '#999' }}
                          />
                        </div>
                        <div className="text-sm text-gray-500 mt-1">{category.description}</div>
                      </div>
                    </List.Item>
                  )}
                />
              ) : (
                <Alert message="No categories found" type="info" />
              )}
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1 p-6 overflow-auto">
            {/* Breadcrumb */}
            {(selectedCategory || selectedArticle) && (
              <Breadcrumb className="mb-4">
                <Breadcrumb.Item>
                  <HomeOutlined />
                  <span 
                    className="cursor-pointer" 
                    onClick={() => {
                      setSelectedCategoryId(null);
                      setSelectedArticleId(null);
                    }}
                  >
                    Help
                  </span>
                </Breadcrumb.Item>
                {selectedCategory && (
                  <Breadcrumb.Item>
                    <span 
                      className="cursor-pointer"
                      onClick={() => setSelectedArticleId(null)}
                    >
                      {selectedCategory.name}
                    </span>
                  </Breadcrumb.Item>
                )}
                {selectedArticle && (
                  <Breadcrumb.Item>{selectedArticle.title}</Breadcrumb.Item>
                )}
              </Breadcrumb>
            )}

            {selectedArticle ? (
              <Card className="shadow-sm">
                <div className="mb-4">
                  <Title level={2} className="mb-2">{selectedArticle.title}</Title>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>Category: {selectedCategory?.name}</span>
                    <span>•</span>
                    <span>Updated: {new Date(selectedArticle.updatedAt).toLocaleDateString()}</span>
                    {selectedArticle.tags && selectedArticle.tags.length > 0 && (
                      <>
                        <span>•</span>
                        <div className="flex gap-1">
                          {selectedArticle.tags.map((tag, index) => (
                            <Badge key={index} text={tag} style={{ backgroundColor: '#f0f0f0' }} />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div
                  className="prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(selectedArticle.content)
                  }}
                />
              </Card>
            ) : selectedCategoryId && articles ? (
              <Card className="shadow-sm">
                <Title level={2} className="mb-4">
                  <FileTextOutlined className="text-green-500 mr-2" />
                  {selectedCategory?.name} Articles
                </Title>
                <Paragraph className="text-gray-600 mb-6">
                  {selectedCategory?.description}
                </Paragraph>

                <List
                  dataSource={articles}
                  renderItem={(article) => (
                    <List.Item
                      className="cursor-pointer p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all mb-3"
                      onClick={() => setSelectedArticleId(article.id)}
                    >
                      <div className="w-full">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="text-lg font-medium text-gray-900 mb-1">{article.title}</div>
                            {article.tags && article.tags.length > 0 && (
                              <div className="flex gap-2 mt-2">
                                {article.tags.map((tag, index) => (
                                  <Tag key={index} color="blue">{tag}</Tag>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 ml-4">
                            {new Date(article.updatedAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </Card>
            ) : searchTerm ? (
              <Card className="shadow-sm">
                <Title level={3}>Search Results for "{searchTerm}"</Title>
                <Paragraph>Search functionality coming soon...</Paragraph>
              </Card>
            ) : (
              <Card className="shadow-sm">
                <Title level={2}>Welcome to Help & Resources</Title>
                <Paragraph className="text-lg">
                  Select a category from the sidebar to browse help articles, or use the search box to find specific information.
                </Paragraph>

                {categories && categories.length > 0 && (
                  <div className="mt-6">
                    <Title level={4}>Available Categories:</Title>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      {categories.map(category => (
                        <Card
                          key={category.id}
                          size="small"
                          className="cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => {
                            setSelectedCategoryId(category.id);
                            setSelectedArticleId(null);
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <Title level={5} className="mb-2">{category.name}</Title>
                              <Paragraph className="text-sm">{category.description}</Paragraph>
                            </div>
                            <Badge
                              count={category.articles?.length || 0}
                              style={{ backgroundColor: '#1890ff' }}
                            />
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpResources;